import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSite } from "@/contexts/SiteContext";
import api from "@/lib/api";

/**
 * AnalyticsLoader — încarcă tracking-ul (GA4 + Google Tag Manager + Meta Pixel)
 * CLIENT-SIDE, DOAR după consimțământul complet de cookies (GDPR).
 *
 * Consimțământul e scris de CookieBanner.jsx în localStorage la cheia
 * `ct_cookie_consent_v1`, ca JSON: { accepted: boolean, ts: number }.
 *   - Butonul „Accept"          → { accepted: true }  ⇒ încărcăm tracking-ul
 *   - Butonul „Doar esențiale"  → { accepted: false } ⇒ NU încărcăm nimic
 *
 * ID-urile publice vin din /api/public/site → câmpul `seo`:
 *   { ga4_id, gtm_id, fb_pixel_id }.
 *
 * Componentă fără UI (returnează null). Toate injectările sunt idempotente
 * (guard pe window.__ct_*_loaded), deci nu apar dubluri la re-render / re-mount.
 */

const CONSENT_KEY = "ct_cookie_consent_v1";

// Citește consimțământul din localStorage. Doar accept COMPLET (accepted === true)
// declanșează tracking-ul. Orice altă valoare / lipsă / JSON invalid ⇒ fără tracking.
function hasFullConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return parsed?.accepted === true;
  } catch {
    return false;
  }
}

// --- Injectoare idempotente ------------------------------------------------

// Google Tag Manager: script în <head> + <noscript> iframe la începutul <body>.
function loadGTM(id) {
  if (!id || window.__ct_gtm_loaded) return;
  window.__ct_gtm_loaded = true;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });

  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(s);

  const ns = document.createElement("noscript");
  const iframe = document.createElement("iframe");
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(id)}`;
  iframe.height = "0";
  iframe.width = "0";
  iframe.style.display = "none";
  iframe.style.visibility = "hidden";
  ns.appendChild(iframe);
  document.body.insertBefore(ns, document.body.firstChild);
}

// GA4 via gtag.js. Dezactivăm page_view-ul automat (`send_page_view: false`)
// și îl trimitem manual — inclusiv la schimbarea rutei în SPA (vezi mai jos).
function loadGA4(id) {
  if (!id || window.__ct_ga_loaded) return;
  window.__ct_ga_loaded = true;

  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  // Definiție canonică gtag (folosește `arguments`, nu rest params).
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = window.gtag || gtag;
  window.gtag("js", new Date());
  window.gtag("config", id, { anonymize_ip: true, send_page_view: false });
}

// Meta (Facebook) Pixel. Codul de bază fbq + init. NU trimitem PageView aici —
// îl trimitem manual (inițial + la fiecare rută) în efectul de page_view.
function loadPixel(id) {
  if (!id || window.__ct_fbq_loaded) return;
  window.__ct_fbq_loaded = true;

  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = true;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable */

  window.fbq("init", id);

  // <noscript> img pentru cazul fără JS (paritate cu snippet-ul oficial).
  const ns = document.createElement("noscript");
  const img = document.createElement("img");
  img.height = 1;
  img.width = 1;
  img.style.display = "none";
  img.alt = "";
  img.src = `https://www.facebook.com/tr?id=${encodeURIComponent(id)}&ev=PageView&noscript=1`;
  ns.appendChild(img);
  document.body.appendChild(ns);
}

// ---------------------------------------------------------------------------

export default function AnalyticsLoader() {
  const { site } = useSite();
  const location = useLocation();

  // `loaded` e state (nu ref) ca efectul de page_view să reacționeze la momentul
  // în care trackerele devin active (ex. consimțământ dat mid-sesiune).
  const [loaded, setLoaded] = useState(false);
  const activatingRef = useRef(false); // guard anti-dublare a activării
  const ga4Ref = useRef(false);
  const fbqRef = useRef(false);

  // Efect 1 — detectează consimțământul și activează trackerele (o singură dată).
  useEffect(() => {
    let cancelled = false;
    let poll = null;

    // Rezolvă ID-urile SEO: preferă contextul deja încărcat, altfel un fetch.
    async function resolveSeo() {
      if (site?.seo) return site.seo;
      try {
        const { data } = await api.get("/public/site");
        return data?.seo || {};
      } catch {
        return {};
      }
    }

    async function activate() {
      if (activatingRef.current) return;
      activatingRef.current = true;

      const seo = await resolveSeo();
      if (cancelled) return;

      if (seo?.gtm_id) loadGTM(seo.gtm_id);
      if (seo?.ga4_id) {
        loadGA4(seo.ga4_id);
        ga4Ref.current = true;
      }
      if (seo?.fb_pixel_id) {
        loadPixel(seo.fb_pixel_id);
        fbqRef.current = true;
      }

      if (!cancelled) setLoaded(true);
    }

    function check() {
      if (hasFullConsent()) {
        activate();
        cleanup();
      }
    }

    function onStorage(e) {
      // `storage` se declanșează doar cross-tab; util dacă acceptul vine din alt tab.
      if (!e || e.key === CONSENT_KEY) check();
    }

    function cleanup() {
      if (poll) {
        clearInterval(poll);
        poll = null;
      }
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("ct-cookie-consent", check);
    }

    if (hasFullConsent()) {
      activate();
    } else {
      // CookieBanner scrie localStorage în ACELAȘI tab, ceea ce NU declanșează
      // evenimentul `storage`. Deci ascultăm mai multe surse:
      //  - `storage`            → accept din alt tab
      //  - `ct-cookie-consent`  → event custom (best-effort, dacă va fi emis vreodată)
      //  - poll ușor la 1s      → prinde acceptul din același tab, fără a modifica banner-ul
      window.addEventListener("storage", onStorage);
      window.addEventListener("ct-cookie-consent", check);
      poll = setInterval(check, 1000);
    }

    return () => {
      cancelled = true;
      cleanup();
    };
    // Re-rulează când `site` devine disponibil (ID-urile SEO vin din context).
  }, [site]);

  // Efect 2 — page_view SPA. Se declanșează când trackerele devin active
  // (primul page_view pentru pagina curentă) și la fiecare schimbare de rută.
  useEffect(() => {
    if (!loaded) return;

    if (ga4Ref.current && typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
    if (fbqRef.current && typeof window.fbq === "function") {
      window.fbq("track", "PageView");
    }
  }, [loaded, location.pathname, location.search]);

  return null;
}
